import { gql, useQuery } from '@apollo/client';
import {
  CSGetTemplatesByEntity,
  Template,
  TemplateStatus,
} from '@admiin-com/ds-graphql';
import { useMemo } from 'react';
import { CSGetSelectedEntityId as GET_SELECTED_ENTITY_ID } from '@admiin-com/ds-graphql';
import { mergeUniqueItems } from '@admiin-com/ds-common';

interface useDocumentsProps {
  searchName?: string;
  limit?: number;
}

export const useDocuments = ({
  searchName = '',
  limit = 20,
}: useDocumentsProps) => {
  const { data: selectedEntityIdData } = useQuery(gql(GET_SELECTED_ENTITY_ID));
  const entityId = selectedEntityIdData?.selectedEntityId;

  const handleLoadMore = () => {
    const currentToken = documentsData?.templatesByEntity?.nextToken;

    if (currentToken) {
      fetchMore({
        variables: {
          nextToken: currentToken,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;
          return {
            ...fetchMoreResult,
            templatesByEntity: {
              ...fetchMoreResult.templatesByEntity,

              items: mergeUniqueItems(
                prevResult.templatesByEntity?.items ?? [],
                fetchMoreResult.templatesByEntity?.items ?? [],
                ['id'] // Assuming 'id' is the unique key
              ),
              nextToken: fetchMoreResult.templatesByEntity.nextToken, // Ensure the new token is updated
            },
          };
        },
      });
    }
  };

  const {
    data: documentsData,
    fetchMore,
    error: searchDocumentsError,
    loading,
  } = useQuery(gql(CSGetTemplatesByEntity), {
    variables: {
      entityId,
      limit,
      filter: {
        searchName: {
          contains: searchName,
        },
        status: { eq: TemplateStatus.ACTIVE },
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    skip: !entityId,
  });

  const documents: Template[] = useMemo(() => {
    const docs =
      documentsData?.templatesByEntity?.items?.filter(
        (template: Template) => template.status !== TemplateStatus.ARCHIVED
      ) || [];

    const getPriority = (type: string | null | undefined) => {
      if (type === 'LETTERHEAD') return 0;
      if (type === 'CUSTOM') return 2;
      return 1;
    };

    return [...docs].sort((a, b) => {
      const priorityDiff = getPriority(a.type) - getPriority(b.type);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      if (getPriority(a.type) === 1) {
        return (a.type ?? '').localeCompare(b.type ?? '');
      }
      return 0;
    });
  }, [documentsData]);

  const total = documentsData?.templatesByEntity?.items?.length || 0;
  const hasNextPage = documentsData?.templatesByEntity?.nextToken != null;

  return {
    documents,
    total,
    error: searchDocumentsError,
    handleLoadMore, // Use this function to load more items
    loading,
    hasNextPage,
  };
};
